import React, { useState, useEffect, useMemo } from "react";
import {
  Checkbox,
  FormControlLabel,
  FormGroup,
  List,
  ListItem,
  Divider,
  Box,
  Typography,
} from "@mui/material";
import theme from "theme";
import { isAdmin, showAlert } from "utils/helper";
import SC from "./style";
import { useSelector } from "react-redux";

const RoleSelection = ({
  items,
  title,
  onSelectionChange,
  selectedItems,
  userProjectId,
}) => {
  const [selectAll, setSelectAll] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");

  const accessLevel = useSelector(
    (state) => state?.authdata?.data?.userType || 0,
  );

  // Effect to update selectAll based on selectedItems
  useEffect(() => {
    const selectData =
      selectedItems.length > 0 && selectedItems.length === items.length;
    setSelectAll(selectData);
  }, [selectedItems, items.length]);

  const handleSelectAllChange = () => {
    if (!userProjectId) {
      showAlert({
        title: "Please select a project",
        text: "You need to choose a project first",
        icon: "warning",
      });

      return;
    }

    setSelectAll((prevSelectAll) => {
      const newSelectAllState = !prevSelectAll;

      if (newSelectAllState) {
        const allRoleIds = items.map((item) => item.id);
        onSelectionChange(allRoleIds, true);
      } else {
        onSelectionChange("clear", false);
      }

      return newSelectAllState;
    });
  };

  // Filter holidays by holidayName
  const handleSearch = (event) => {
    setSearchQuery(event.target.value.trim().toLowerCase());
  };

  // Derived filtered lists using useMemo
  const { filteredWithAccessControl, filteredWithoutAccessControl } =
    useMemo(() => {
      const filtered = items.filter((item) =>
        item.role?.toLowerCase().includes(searchQuery),
      );
      return {
        filteredWithAccessControl: filtered.filter(
          (item) => item.accessControlType != null,
        ),
        filteredWithoutAccessControl: filtered.filter(
          (item) => item.accessControlType == null,
        ),
      };
    }, [items, searchQuery]);

  const renderCheckboxList = (items, itemWidth = "33%") => (
    <List
      sx={{
        display: "flex",
        flexWrap: "wrap",
        alignContent: "flex-start",
        alignItems: "flex-start",
      }}
    >
      {items.map((item) => (
        <ListItem
          key={item.id}
          disablePadding
          sx={{ width: `calc(${itemWidth} - 0px)` }}
        >
          <FormGroup>
            <FormControlLabel
              control={
                <Checkbox
                  checked={selectedItems.includes(item.id)}
                  onChange={() => onSelectionChange(item.id)}
                  sx={{
                    "&.Mui-checked": {
                      color: theme.colors.themeColor,
                    },
                  }}
                  size="small"
                />
              }
              label={<span style={{ fontSize: "12px" }}>{item.role}</span>}
            />
          </FormGroup>
        </ListItem>
      ))}
    </List>
  );

  return (
    <>
      <SC.InputContiner>
        <SC.SearchIcon />
        <SC.InputStyle placeholder="Search..." onChange={handleSearch} />
      </SC.InputContiner>
      <Box sx={{ display: "flex" }}>
        {/* Left Panel */}
        <Box
          sx={{
            borderRight: `1px solid ${theme.colors.bordertask}`,
            flex: 2.5,
          }}
        >
          {title && (
            <Box sx={{ display: "flex", alignItems: "center" }}>
              <Typography variant="h6" sx={{ flexGrow: 1, textAlign: "left" }}>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={selectAll}
                      onChange={handleSelectAllChange}
                      sx={{
                        "&.Mui-checked": {
                          color: theme.colors.themeColor,
                        },
                      }}
                      size="small"
                    />
                  }
                  label={<span style={{ fontWeight: "bold" }}>{title}</span>}
                />
              </Typography>
            </Box>
          )}
          <Divider />
          {renderCheckboxList(filteredWithoutAccessControl)}
        </Box>

        {/* Right Panel */}
        {isAdmin(accessLevel) && (
          <Box sx={{ flex: 1 }}>
            <Box sx={{ display: "flex", alignItems: "center" }}>
              <p
                style={{
                  flexGrow: 1,
                  textAlign: "left",
                  margin: "10px",
                  fontWeight: "bold",
                  fontSize: "12px",
                }}
              >
                Administrative Role
              </p>
            </Box>
            <Divider />
            {renderCheckboxList(filteredWithAccessControl, "100%")}
          </Box>
        )}
      </Box>
    </>
  );
};

export default RoleSelection;
